import { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useQuery, gql } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';

const GET_CAR_RATINGS = gql`
  query GetCarRatings($carId: ID!, $limit: Int, $offset: Int) {
    getCarRatings(carId: $carId, limit: $limit, offset: $offset) {
      summary {
        averageRating
        totalRatings
        maintenanceAvg
        cleanlinessAvg
        communicationAvg
        convenienceAvg
        accuracyAvg
        ratingDistribution {
          fiveStars
          fourStars
          threeStars
          twoStars
          oneStar
        }
      }
      reviews {
        rating {
          id
          guestRating {
            general {
              score
            }
            comment
          }
          guestRatedAt
          hostReply
          hostRepliedAt
        }
        guestName
        guestAvatarUrl
        tripDate
      }
      totalCount
    }
  }
`;

const Container = styled.div`
  ${tw`mt-8 pt-8 border-t border-border-default`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold mb-6`}
`;

const SummaryContainer = styled.div`
  ${tw`mb-8`}
`;

const OverallRating = styled.div`
  ${tw`flex items-center gap-2 mb-4`}
`;

const RatingNumber = styled.span`
  ${tw`text-4xl font-bold`}
  color: #C9B896;
`;

const StarIcon = styled(FontAwesomeIcon)`
  color: #c9b896;
  ${tw`text-2xl`}
`;

const RatingCount = styled.span`
  ${tw`text-text-secondary text-sm ml-2`}
`;

const CategoryBarsContainer = styled.div`
  ${tw`grid gap-3 mb-4`}
`;

const CategoryRow = styled.div`
  ${tw`flex items-center gap-3`}
`;

const CategoryLabel = styled.span`
  ${tw`text-sm text-text-secondary w-28`}
`;

const ProgressBarBg = styled.div`
  ${tw`flex-1 h-2 bg-bg-secondary rounded-full overflow-hidden`}
`;

const ProgressBarFill = styled.div<{ $width: number }>`
  ${tw`h-full bg-black rounded-full transition-all duration-300`}
  width: ${({ $width }) => $width}%;
`;

const CategoryScore = styled.span`
  ${tw`text-sm text-text-secondary w-8 text-right`}
`;

const BasedOnText = styled.p`
  ${tw`text-xs text-text-secondary mt-2`}
`;

const ReviewsContainer = styled.div`
  ${tw`mt-6`}
`;

const ReviewsTitle = styled.h3`
  ${tw`text-sm font-semibold text-text-secondary uppercase tracking-wide mb-4`}
`;

const ReviewCard = styled.div`
  ${tw`py-4 border-b border-border-default last:border-b-0`}
`;

const ReviewHeader = styled.div`
  ${tw`flex items-start gap-3 mb-2`}
`;

const Avatar = styled.div`
  ${tw`w-10 h-10 rounded-full bg-bg-secondary flex items-center justify-center text-text-secondary font-medium text-sm overflow-hidden flex-shrink-0`}
`;

const ReviewMeta = styled.div`
  ${tw`flex-1`}
`;

const ReviewStars = styled.div`
  ${tw`flex gap-1 mb-1`}
`;

const ReviewerName = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const ReviewDate = styled.span`
  ${tw`text-sm text-text-tertiary ml-1`}
`;

const ReviewComment = styled.p`
  ${tw`text-text-secondary text-sm mt-2`}
`;

const SeeMoreButton = styled.button`
  ${tw`mt-6 px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-black transition-colors cursor-pointer`}
`;

const NoRatingsText = styled.p`
  ${tw`text-text-secondary text-center py-8`}
`;

interface CarRatingsSectionProps {
  carId: string;
  initialLimit?: number;
}

export const CarRatingsSection = ({ carId, initialLimit = 5 }: CarRatingsSectionProps) => {
  const [limit, setLimit] = useState(initialLimit);

  const { data, loading, error, fetchMore } = useQuery(GET_CAR_RATINGS, {
    variables: { carId, limit, offset: 0 },
    skip: !carId,
  });

  if (loading) {
    return (
      <Container>
        <Title>Ratings and reviews</Title>
        <NoRatingsText>Loading ratings...</NoRatingsText>
      </Container>
    );
  }

  if (error || !data?.getCarRatings) {
    return null;
  }

  const { summary, reviews, totalCount } = data.getCarRatings;

  if (summary.totalRatings === 0) {
    return (
      <Container>
        <Title>Ratings and reviews</Title>
        <NoRatingsText>No ratings yet</NoRatingsText>
      </Container>
    );
  }

  const categories = [
    { label: 'Cleanliness', score: summary.cleanlinessAvg },
    { label: 'Maintenance', score: summary.maintenanceAvg },
    { label: 'Communication', score: summary.communicationAvg },
    { label: 'Convenience', score: summary.convenienceAvg },
    { label: 'Accuracy', score: summary.accuracyAvg },
  ];

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleSeeMore = () => {
    const newLimit = limit + 5;
    setLimit(newLimit);
    fetchMore({
      variables: { carId, limit: newLimit, offset: 0 },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return fetchMoreResult;
      },
    });
  };

  return (
    <Container>
      <Title>Ratings and reviews</Title>

      <SummaryContainer>
        <OverallRating>
          <RatingNumber>{summary.averageRating.toFixed(2)}</RatingNumber>
          <StarIcon icon={faStar} />
        </OverallRating>
        <RatingCount>({summary.totalRatings} ratings)</RatingCount>

        <CategoryBarsContainer>
          {categories.map(cat => (
            <CategoryRow key={cat.label}>
              <CategoryLabel>{cat.label}</CategoryLabel>
              <ProgressBarBg>
                <ProgressBarFill $width={(cat.score / 5) * 100} />
              </ProgressBarBg>
              <CategoryScore>{cat.score.toFixed(1)}</CategoryScore>
            </CategoryRow>
          ))}
        </CategoryBarsContainer>

        <BasedOnText>Based on {summary.totalRatings} guest ratings</BasedOnText>
      </SummaryContainer>

      <ReviewsContainer>
        <ReviewsTitle>Reviews</ReviewsTitle>

        {reviews.map((review: any) => (
          <ReviewCard key={review.rating.id}>
            <ReviewHeader>
              <Avatar>
                {review.guestAvatarUrl ? (
                  <img
                    src={review.guestAvatarUrl}
                    alt={review.guestName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  getInitials(review.guestName || 'G')
                )}
              </Avatar>
              <ReviewMeta>
                <ReviewStars>
                  {[1, 2, 3, 4, 5].map(star => (
                    <FontAwesomeIcon
                      key={star}
                      icon={faStar}
                      style={{
                        color:
                          star <= (review.rating.guestRating?.general?.score || 0)
                            ? '#C9B896'
                            : '#E5E7EB',
                        fontSize: '14px',
                      }}
                    />
                  ))}
                </ReviewStars>
                <div>
                  <ReviewerName>{review.guestName}</ReviewerName>
                  <ReviewDate>
                    •{' '}
                    {format(
                      new Date(review.tripDate || review.rating.guestRatedAt),
                      'MMMM d, yyyy',
                    )}
                  </ReviewDate>
                </div>
              </ReviewMeta>
            </ReviewHeader>

            {review.rating.guestRating?.comment && (
              <ReviewComment>{review.rating.guestRating.comment}</ReviewComment>
            )}
          </ReviewCard>
        ))}

        {totalCount > reviews.length && (
          <SeeMoreButton onClick={handleSeeMore}>See more</SeeMoreButton>
        )}
      </ReviewsContainer>
    </Container>
  );
};
